import styled, { css } from "styled-components";
import { Link as ReactRouterLink } from "react-router-dom";
import Grid from "@material-ui/core/Grid";

import FormInput from "core/FormInput";
import { SHARED_FORM_STYLES } from "core/Form";
import Flexbox from "core/Flexbox";
import { P, BorderBottomLinkStyle, CustomFocusStyle } from "core/Typography";
import { Col } from "core/grid";
import fonts from "core/fonts";
import breakpoints from "core/breakpoints";
import { spacingWithBreakpoints } from "core/spacing";
import colors from "core/colors";
import BodyText from "core/BodyText";
import { focusOutline } from "core/links";
import { easingSeconds } from "core/animation";
import { regular } from "core/fontStyles";

import Question from "core/ReusableIcons/Question";
import Add from "core/ReusableIcons/Add";

import { convertPxToRem } from "frontend/components/helpers";
import { handleTopSpacing, handleBottomSpacing } from "helpers/spacingAndFonts";

// for both forms:

// CardLinks component:
export const FormButtonBodyText = styled(BodyText)`
    height: 1.438rem; /* 23px */
    ${BorderBottomLinkStyle}

    &:hover {
        color: ${colors.ink};
        border-color: ${colors.ink};
    }

    ${CustomFocusStyle}
`;

export const CardLinksFlexbox = styled(Flexbox)`
    height: 1.438rem; /* 23px */
    margin-bottom: 1.5rem; /* 24px */
`;

// OrderSummary component:
export const OrderItem = styled(Flexbox)`
    ${handleBottomSpacing({ bottomSpacing: "lg" })}
    ${handleTopSpacing({ topSpacing: "lg" })}
`;

export const TotalCharge = styled(Flexbox)`
    border-top: ${convertPxToRem({ px: 1 })} solid ${colors.concrete};
    ${handleTopSpacing({ topSpacing: "lg" })}
`;

export const SubsOrderItem = styled(BodyText).attrs({
    size: "sm",
    type: "p"
})`
    ${handleBottomSpacing({ bottomSpacing: "xl" })}
    ${handleTopSpacing({ topSpacing: "lg" })}
`;

export const Thumbnail = styled.img`
    max-width: 5.875rem; /* 94px */
    max-height: 5.875rem; /* 94px */
    margin-right: 0.875rem; /* 14px */
    width: auto;
    height: auto;
`;

export const UsdBodyText = styled(BodyText)`
    margin-right: 0.5rem; /* 8px */
`;

export const StyledOrderSummary = styled.div`
    ${spacingWithBreakpoints.XL2TopAndBottom} //border: 1px solid green;
`;

// FormButtons component:
export const PurchaseLinkGridItem = styled(Grid)``;

export const CancelLink = styled(ReactRouterLink)`
    ${BorderBottomLinkStyle}
    ${CustomFocusStyle}
    ${regular}
    
    color: ${colors.fossil};
    border-color: ${colors.fossil};
    font-size: ${convertPxToRem({ px: 15 })};
    
    &:hover {
        color: ${colors.ink};
        border-color: ${colors.ink};
    }
`;

export const CancelLinkGridItem = styled(Grid)`
    text-align: right;
`;

// StripeFormInput component:
export const StripeFormInputContainer = styled.div`
    label {
        display: block;
    }
`;

export const SuccessIconWrapper = styled.div`
    opacity: 0;
    position: absolute;
    right: 0.8875rem;
    top: 50%;
    margin-top: -0.625rem;
    transition: all ${easingSeconds};
`;

export const InputWrapper = styled.div`
    position: relative;

    .StripeElement {
        ${SHARED_FORM_STYLES.BASE}
        transition: box-shadow ${easingSeconds};
        padding-top: ${convertPxToRem({ px: 11 })};
        padding-bottom: ${convertPxToRem({ px: 11 })};

        &:hover {
            ${SHARED_FORM_STYLES.HOVER}
        }

        &--focus {
            &,
            &:hover {
                ${SHARED_FORM_STYLES.FOCUS}
            }
        }

        &--complete:not(.StripeElement--focus) + ${SuccessIconWrapper} {
            opacity: 1;
        }
    }

    .input-complete + ${SuccessIconWrapper} {
        opacity: 1;
    }
`;

// used in EmptyForm but is for StripeFormInput-
// styles wont reflect via the style object because Stripe forms use object style format & other various differences:
export const stripeElementStyles = {
    base: {
        fontSize: "16px", // using REM makes it look much smaller
        color: colors.midnight,
        ":disabled": {
            color: colors.storm
        },
        "::placeholder": {
            color: colors.storm,
            transition: `all ${easingSeconds}`
        },
        ":focus": {
            "::placeholder": {
                color: "transparent"
            }
        }
    },
    invalid: {
        color: colors.brick
    }
};

// FormInputLayout:
export const ReceiptInfoHelpIcon = styled(Question).attrs({
    size: "md",
    fillColor: colors.storm,
    "data-tip": "show and hide",
    "data-event": "click",
    "data-event-off": "blur"
})`
    margin-left: 0.375rem; /* 6px*/
    cursor: pointer;
`;

export const ReceiptInfoTextWrapper = styled(Flexbox)`
    ${fonts.typeScale[14]}
`;

export const MinusIcon = styled.div`
    background-color: ${colors.storm};
    width: 0.75rem; /* 12px */
    height: 0.125rem; /* 2px */
    margin: 0 auto;
`;

export const PlusMinusButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: 0;
    padding: 0;
    margin: 0;
    background: none;
    width: 1rem; /* 16px */
    height: 1rem; /* 16px */

    margin-right: 0.375rem; /* 6px */

    &:focus {
        outline: none;
        border: none;
    }

    ${Add} {
        position: relative;
        width: 1rem;
        height: 1rem;

        path {
            fill: ${colors.storm};
        }
    }

    &:focus {
        ${focusOutline}
    }
`;

export const StyledPhotoCheckoutForm = styled.div`
    form {
        width: 100%;
    }
`;

export const FixedWidthCol = styled(Col)`
    width: 100%;

    @media (min-width: ${breakpoints.tablet.rem}) {
        width: ${({ width }) => width};
    }
`;

export const GeneralFormError = styled(P).attrs({ size: 12 })`
    color: ${colors.coral};
    margin-bottom: 0.5rem;
`;

export const CardNumberGrid = styled(Grid)``;

export const MonthYearGrid = styled(Grid)`
    width: 7.188rem; /* 115px */
    padding-right: 1rem; /* 16px */
`;

export const CvcGrid = styled(Grid)`
    width: 4.938rem; /* 79px */
`;

export const NameGrid = styled(Grid)``;

export const PostalGrid = styled(Grid)`
    width: 8.375rem; /* 134px */
`;

export const OptionalBodyText = styled(BodyText)`
    margin-left: 0.1875rem; /* 3px */
`;

export const VatGrid = styled(Grid)`
    ${FormInput} {
        margin-bottom: 0; // to target the VAT field when it's open
    }

    ${FormButtonBodyText},
    ${OptionalBodyText} {
        line-height: 0.9375rem; /* 15px */
        height: auto;
    }
`;
